import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import { formatINR } from "@/lib/constants";
import {
  Package,
  Upload,
  Copy,
  Trash2,
  Share2,
  Download,
  MessageCircle,
  Save,
  Loader2,
} from "lucide-react";

export default function ShareKit({ products = [], refetchAll }) {
  const { isOwner } = useAuth();
  const [selId, setSelId] = useState(products[0]?.id || null);
  const [media, setMedia] = useState([]);
  const [price, setPrice] = useState(0);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);

  const selected = products.find((p) => p.id === selId) || null;

  useEffect(() => {
    if (!selId && products.length) setSelId(products[0].id);
  }, [products, selId]);

  useEffect(() => {
    if (!selected) return;
    setPrice(selected.price || 0);
    setMessage(selected.preset_message || "");
    const load = async () => {
      setLoadingMedia(true);
      try {
        const res = await axios.get(`${API}/products/${selected.id}/media`);
        setMedia(res.data);
      } catch {
        setMedia([]);
      } finally {
        setLoadingMedia(false);
      }
    };
    load();
  }, [selId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fileUrl = (m) => `${API}/media/${m.id}/file`;

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await axios.patch(`${API}/products/${selected.id}`, {
        price: Number(price) || 0,
        preset_message: message,
      });
      toast.success("Product details saved");
      await refetchAll();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingInfo(false);
    }
  };

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/products/${selected.id}/media`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Media uploaded");
      const res = await axios.get(`${API}/products/${selected.id}/media`);
      setMedia(res.data);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`);
      setMedia((m) => m.filter((x) => x.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyMessage = async () => {
    if (!message.trim()) {
      toast.error("Add a message first");
      return;
    }
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const whatsappText = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message || selected.name)}`, "_blank");
  };

  const downloadMedia = async (m) => {
    try {
      const res = await axios.get(fileUrl(m), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const shareMedia = async (m) => {
    try {
      const res = await axios.get(fileUrl(m), { responseType: "blob" });
      const file = new File([res.data], m.original_filename, { type: m.content_type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: message || selected.name });
      } else {
        toast.info("Direct share not supported here — downloading so you can attach in WhatsApp");
        await downloadMedia(m);
      }
    } catch {
      toast.error("Share not available");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl w-full mx-auto" data-testid="sharekit-page">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">
          Sales enablement
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Product library & share kit
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Store prices, a ready-to-send message, and images/videos for each product — share on WhatsApp in one tap.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
        {/* Product list */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-2 h-fit">
          {products.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              No products yet. Add some in Products.
            </div>
          )}
          {products.map((p) => (
            <button
              key={p.id}
              data-testid={`sharekit-product-${p.id}`}
              onClick={() => setSelId(p.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left ${
                selId === p.id
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
              style={{ transition: "background-color 150ms ease" }}
            >
              <Package size={15} />
              <span className="flex-1 truncate">{p.name}</span>
              {p.price > 0 && (
                <span className="text-[11px] text-slate-500">{formatINR(p.price)}</span>
              )}
            </button>
          ))}
        </div>

        {/* Detail */}
        {selected ? (
          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <h2 className="font-heading text-xl font-bold text-slate-900">{selected.name}</h2>
                <div className="flex items-end gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1 block">
                      Price (₹)
                    </label>
                    <input
                      data-testid="sharekit-price-input"
                      type="number"
                      min="0"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-32 h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                  <button
                    data-testid="sharekit-save-info"
                    onClick={saveInfo}
                    disabled={savingInfo}
                    className="inline-flex items-center gap-1.5 h-9 px-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-md"
                    style={{ transition: "background-color 160ms ease" }}
                  >
                    <Save size={15} />
                    Save
                  </button>
                </div>
              </div>

              <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block">
                Prerequisite message
              </label>
              <textarea
                data-testid="sharekit-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`e.g. Hi! Here are the details for ${selected.name}. Price: ₹___ / unit. Let me know your quantity and I'll share a quote.`}
                className="w-full min-h-[110px] px-3 py-2.5 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  data-testid="sharekit-copy-message"
                  onClick={copyMessage}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Copy size={15} /> Copy message
                </button>
                <button
                  data-testid="sharekit-whatsapp-text"
                  onClick={whatsappText}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
                >
                  <MessageCircle size={15} /> Send on WhatsApp
                </button>
              </div>
            </div>

            {/* Media */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-base font-semibold text-slate-800">
                  Images & videos ({media.length})
                </h3>
                <label
                  data-testid="sharekit-upload-label"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold cursor-pointer"
                  style={{ transition: "background-color 160ms ease" }}
                >
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  {uploading ? "Uploading…" : "Upload media"}
                  <input
                    data-testid="sharekit-file-input"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => upload(Array.from(e.target.files))}
                  />
                </label>
              </div>

              {loadingMedia ? (
                <div className="py-10 text-center text-sm text-slate-500">Loading media…</div>
              ) : media.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                  No media yet. Upload product images or videos to share with clients.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {media.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`media-item-${m.id}`}
                      className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50 group"
                    >
                      <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                        {m.kind === "video" ? (
                          <video src={fileUrl(m)} className="w-full h-full object-cover" controls />
                        ) : (
                          <img src={fileUrl(m)} alt={m.original_filename} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex items-center justify-between px-2 py-1.5 gap-1">
                        <button
                          data-testid={`media-share-${m.id}`}
                          onClick={() => shareMedia(m)}
                          title="Share"
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                        >
                          <Share2 size={15} />
                        </button>
                        <button
                          data-testid={`media-download-${m.id}`}
                          onClick={() => downloadMedia(m)}
                          title="Download"
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Download size={15} />
                        </button>
                        {isOwner && (
                          <button
                            data-testid={`media-delete-${m.id}`}
                            onClick={() => removeMedia(m.id)}
                            title="Delete"
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded ml-auto"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
            Select a product to manage its share kit.
          </div>
        )}
      </div>
    </div>
  );
}
